import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AppDialog,
  MiniLoader,
  Select,
  type SelectOption,
} from '../Common'
import { useNls } from '../../nls/nlsProvider'
import type { TimelineElement } from '../../context/TimelineContext'
import {
  bookEduPtmSlot,
  mapEduPtmError,
  type EduPtmDayItem,
} from '../../services/eduPtmService'
import {
  listLearners,
  listLearnersForMember,
  type EduLearnerDoc,
} from '../../services/eduLearnerService'
import { generateEduRequestId } from '../../services/eduEventService'
import { filterLearnersForAudience } from './eduPtmHelpers'
import './eduPtm.css'

export interface EduPtmBookOnBehalfDialogProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  day: EduPtmDayItem
  teacherUid: string
  slotStart: string
  elements: TimelineElement[]
  isAdmin: boolean
  currentUserId: string
  onBooked: () => void
}

export const EduPtmBookOnBehalfDialog = ({
  isOpen,
  onClose,
  workspaceId,
  day,
  teacherUid,
  slotStart,
  elements,
  isAdmin,
  currentUserId,
  onBooked,
}: EduPtmBookOnBehalfDialogProps) => {
  const { t } = useNls()
  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  }, [t])
  const requestIdRef = useRef<string>(generateEduRequestId('ptm_behalf'))

  const [learners, setLearners] = useState<EduLearnerDoc[]>([])
  const [selectedLearnerId, setSelectedLearnerId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    let isMounted = true
    const fetchLearners = async () => {
      try {
        setIsLoading(true)
        setErrorMsg(null)
        const all = isAdmin
          ? await listLearners(workspaceId)
          : await listLearnersForMember(workspaceId, currentUserId)

        if (isMounted) {
          const filtered = filterLearnersForAudience(all, day.audience, elements)
          setLearners(filtered)
          if (filtered[0]) {
            setSelectedLearnerId(filtered[0].id)
          }
        }
      } catch (err: unknown) {
        if (isMounted) setErrorMsg(tRef.current(mapEduPtmError(err)))
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    void fetchLearners()
    return () => {
      isMounted = false
    }
  }, [isOpen, workspaceId, isAdmin, currentUserId, day.audience, elements])

  const learnerOptions = useMemo<SelectOption<string>[]>(() => {
    return learners.map((l) => ({
      value: l.id,
      label: l.admissionNo ? `${l.displayName} (${l.admissionNo})` : l.displayName,
    }))
  }, [learners])

  const handleConfirm = async () => {
    if (!selectedLearnerId) return
    try {
      setIsSubmitting(true)
      setErrorMsg(null)

      await bookEduPtmSlot({
        workspaceId,
        dayId: day.id,
        teacherUid,
        slotStart,
        learnerId: selectedLearnerId,
        onBehalf: true,
        requestId: requestIdRef.current,
      })

      onBooked()
      onClose()
    } catch (err: unknown) {
      setErrorMsg(tRef.current(mapEduPtmError(err)))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AppDialog
      open={isOpen}
      onDismiss={onClose}
      title={t('NLSEduPtmBookOnBehalfTitle')}
      size="sm"
      actions={[
        {
          label: t('NLSCommonActionCancel'),
          onClick: onClose,
          variant: 'secondary',
          disabled: isSubmitting,
        },
        {
          label: t('NLSEduPtmBookConfirmButton'),
          onClick: handleConfirm,
          variant: 'primary',
          disabled: isSubmitting || isLoading || !selectedLearnerId,
        },
      ]}
    >
      <div className="eduPtmEditor">
        {errorMsg && (
          <div className="eduDialog__banner eduDialog__banner--error" role="alert">
            {errorMsg}
          </div>
        )}

        {isLoading ? (
          <MiniLoader />
        ) : (
          <div className="eduPtmEditor__field">
            <span className="eduPtmEditor__label">{t('NLSEduPtmSelectLearnerLabel')}</span>
            {learners.length === 0 ? (
              <p className="eduPtmEditor__help">{t('NLSEduPtmErrorNotInAudience')}</p>
            ) : (
              <Select<string>
                value={selectedLearnerId}
                onChange={(val) => setSelectedLearnerId(val)}
                options={learnerOptions}
                disabled={isSubmitting}
                ariaLabel={t('NLSEduPtmSelectLearnerLabel')}
              />
            )}
          </div>
        )}
      </div>
    </AppDialog>
  )
}
